import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const RotationsList = ({ timecard }) => {
  const navigate = useNavigate();
  const [rotations, setRotations] = useState(timecard?.parsed_json?.rotations || []);
  const [links, setLinks] = useState([]);
  const [isLoadingLinks, setIsLoadingLinks] = useState(true);

  useEffect(() => {
    // Show rotations immediately
    const baseRotations = timecard?.parsed_json?.rotations || [];
    setRotations(baseRotations);

    if (timecard?.timecard_id) {
      loadLinkedRotations();
      enrichRotationsWithFlags();
    }
  }, [timecard?.timecard_id, timecard?.parsed_json?.rotations]);

  const enrichRotationsWithFlags = async () => {
    const baseRotations = timecard?.parsed_json?.rotations || [];
    if (baseRotations.length === 0) return;

    // Collect all rotation numbers that need enrichment
    const rotationNumbers = baseRotations
      .filter(r => r.rotation_number && r.rotation_number !== 'N/A' && !r.is_special_code)
      .map(r => r.rotation_number);

    if (rotationNumbers.length === 0) return;

    try {
      // Fetch ALL rotations in one API call using comma-separated rotation numbers
      const response = await fetch(
        `http://localhost:8000/api/v1/rotations?rotation_numbers=${rotationNumbers.join(',')}`
      );
      
      if (response.ok) {
        const data = await response.json();
        const rotationMap = {};
        
        // Build lookup map
        (data.rotations || []).forEach(rot => {
          rotationMap[rot.rotation_number] = {
            flags: rot.flags || [],
            rotation_id: rot.id
          };
        });

        // Enrich rotations with flags and IDs
        const enrichedRotations = baseRotations.map(rotation => {
          const enrichData = rotationMap[rotation.rotation_number];
          if (enrichData) {
            return { ...rotation, ...enrichData };
          }
          return { ...rotation, flags: [] };
        });

        setRotations(enrichedRotations);
      }
    } catch (error) {
      console.error('Failed to load rotation flags:', error);
    }
  };

  const loadLinkedRotations = async () => {
    try {
      setIsLoadingLinks(true);
      const url = `http://localhost:8000/api/v1/linking/status?timecard_id=${timecard.timecard_id}`;
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setLinks(data.links || []);
      } else {
        setLinks([]);
      }
    } catch (error) {
      console.error('Failed to load linked rotations:', error);
      setLinks([]);
    } finally {
      setIsLoadingLinks(false);
    }
  };

  // Check if a rotation is linked
  const isRotationLinked = (rotationNumber) => {
    if (!rotationNumber || rotationNumber === 'N/A') return false;
    return links.some(link => link.rotation_number === rotationNumber);
  };

  const handleRotationClick = (rotation) => {
    if (rotation.is_special_code || !rotation.rotation_number || rotation.rotation_number === 'N/A') {
      return; // Don't navigate for special codes
    }
    // Use the stored rotation_id if available
    if (rotation.rotation_id) {
      navigate(`/rotation-detail?id=${rotation.rotation_id}`);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === '0:00') return '—';
    return timeStr;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    return `${day}${month}`;
  };

  // Get flag badge styling
  const getFlagBadgeStyle = (flagType) => {
    const styles = {
      reroute: 'badge-error',
      sick: 'badge-warning',
      apd_pd: 'badge-info',
      '23k': 'badge-success',
      ws: 'badge-accent',
      gs: 'badge-accent',
      sl: 'badge-accent',
      qs: 'badge-accent',
      vac: 'badge-success',
      other: 'badge-secondary'
    };
    return styles[flagType] || 'badge-secondary';
  };

  if (rotations.length === 0) {
    return (
      <div className="card p-8 text-center">
        <Icon name="Plane" className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
        <p className="text-muted-foreground">No rotations found in this timecard</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Calendar" className="w-5 h-5 text-accent-cyan" />
          Rotation Summary Table
        </h3>
        <span className="text-sm text-muted-foreground">
          {rotations.length} rotation{rotations.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Show info banner if any spillover rotations exist */}
      {rotations.some(r => r.is_spillover) && (
        <div className="card p-4 bg-warning/10 border-warning/30">
          <div className="flex items-start gap-3">
            <Icon name="Info" className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-foreground mb-1">Spillover Trips</h4>
              <p className="text-sm text-muted-foreground">
                Some trips started this month but finished next month. 
                Credit shown is only for this month's portion - remaining credit appears on next timecard.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAFB Note */}
      <div className="card p-4 bg-info/10 border-info/30">
        <div className="flex items-start gap-3">
          <Icon name="Info" className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-foreground mb-1">TAFB Calculations</h4>
            <p className="text-sm text-muted-foreground">
              TAFB (Time Away From Base) is calculated from linked parsed rotation data. 
              If a rotation is not linked, TAFB cannot be calculated for per diem purposes.
            </p>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table-auto w-full text-sm">
            <thead className="bg-accent-cyan/10 border-b-2 border-accent-cyan/30">
              <tr>
                <th className="text-left px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Date</th>
                <th className="text-left px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Day</th>
                <th className="text-left px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Rotation #</th>
                <th className="text-right px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Block<br/>HRS</th>
                <th className="text-right px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Sked<br/>Time</th>
                <th className="text-right px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Pay<br/>Time</th>
                <th className="text-right px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Credit</th>
                <th className="text-right px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Addtl Pay<br/>Only</th>
                <th className="text-right px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">TAFB</th>
                <th className="text-center px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Special<br/>Code</th>
                <th className="text-left px-3 py-3 text-xs font-bold text-foreground uppercase tracking-wide">Tags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-accent-cyan/5">
              {rotations.map((rotation, index) => (
                <tr
                  key={index}
                  onClick={() => handleRotationClick(rotation)}
                  className={`hover:bg-accent-cyan/5 transition-colors ${
                    rotation.rotation_id && !rotation.is_special_code
                      ? 'cursor-pointer'
                      : ''
                  }`}
                >
                  {/* DATE */}
                  <td className="px-3 py-3 text-xs font-mono text-foreground">
                    {formatDate(rotation.date)}
                  </td>
                  
                  {/* DAY */}
                  <td className="px-3 py-3 text-xs font-mono text-muted-foreground">
                    {rotation.designation || '—'}
                  </td>
                  
                  {/* ROTATION # */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      {rotation.rotation_number && rotation.rotation_number !== 'N/A' ? (
                        <>
                          <span className="font-mono text-sm font-semibold text-accent-cyan">
                            {rotation.rotation_number}
                          </span>
                          {rotation.is_spillover && (
                            <Icon 
                              name="ArrowRightLeft" 
                              className="w-3.5 h-3.5 text-warning" 
                              title="Spillover: Split across months"
                            />
                          )}
                          {isRotationLinked(rotation.rotation_number) && (
                            <Icon 
                              name="Link" 
                              className="w-3.5 h-3.5 text-accent-purple" 
                              title="Linked to parsed rotation"
                            />
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </td>
                  
                  {/* BLOCK HRS */}
                  <td className="px-3 py-3 text-right">
                    <span className="font-mono text-xs text-foreground">
                      {formatTime(rotation.block_hours)}
                    </span>
                  </td>
                  
                  {/* SKED TIME */}
                  <td className="px-3 py-3 text-right">
                    <span className="font-mono text-xs text-foreground">
                      {formatTime(rotation.sked_hours)}
                    </span>
                  </td>
                  
                  {/* PAY TIME */}
                  <td className="px-3 py-3 text-right">
                    <span className="font-mono text-xs text-foreground">
                      {formatTime(rotation.pay_time)}
                    </span>
                  </td>
                  
                  {/* CREDIT */}
                  <td className="px-3 py-3 text-right">
                    <span className={`font-mono text-xs font-semibold ${
                      rotation.credit_hours && rotation.credit_hours !== '0:00'
                        ? 'text-accent-cyan'
                        : 'text-muted-foreground'
                    }`}>
                      {formatTime(rotation.credit_hours)}
                    </span>
                  </td>
                  
                  {/* ADDTL PAY ONLY */}
                  <td className="px-3 py-3 text-right">
                    <span className="font-mono text-xs text-foreground">
                      {formatTime(rotation.additional_pay_only)}
                    </span>
                  </td>
                  
                  {/* TAFB */}
                  <td className="px-3 py-3 text-right">
                    <span className="font-mono text-xs text-foreground">
                      {formatTime(rotation.tafb_hours)}
                    </span>
                  </td>
                  
                  {/* SPECIAL CODE */}
                  <td className="px-3 py-3 text-center">
                    {rotation.is_special_code ? (
                      <span className="inline-block px-2 py-0.5 bg-warning/20 text-warning text-xs font-mono rounded border border-warning/30">
                        {rotation.rotation_number}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  
                  {/* TAGS (Rotation Flags) */}
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {rotation.flags && rotation.flags.length > 0 ? (
                        rotation.flags.map((flag, idx) => {
                          // Handle both string flags and object flags
                          const flagType = typeof flag === 'string' ? flag : flag.flag_type;
                          const flagNotes = typeof flag === 'string' ? '' : flag.notes;
                          return (
                            <span 
                              key={idx}
                              className={`badge text-xs ${getFlagBadgeStyle(flagType)}`}
                              title={flagNotes || flagType}
                            >
                              {flagType}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-accent-cyan/5 border-t-2 border-accent-cyan/30">
              <tr>
                <td colSpan="3" className="px-3 py-3 text-xs font-bold text-foreground uppercase">
                  TOTAL
                </td>
                
                {/* BLOCK HRS Total */}
                <td className="px-3 py-3 text-right">
                  <span className="font-mono text-xs font-bold text-foreground">
                    {(() => {
                      const total = rotations.reduce((sum, r) => {
                        if (!r.block_hours || r.block_hours === '0:00') return sum;
                        const [h, m] = r.block_hours.split(':').map(Number);
                        return sum + h + (m / 60);
                      }, 0);
                      const hours = Math.floor(total);
                      const minutes = Math.round((total - hours) * 60);
                      return `${hours}:${minutes.toString().padStart(2, '0')}`;
                    })()}
                  </span>
                </td>
                
                {/* SKED TIME Total */}
                <td className="px-3 py-3 text-right">
                  <span className="font-mono text-xs font-bold text-foreground">
                    {(() => {
                      const total = rotations.reduce((sum, r) => {
                        if (!r.sked_hours || r.sked_hours === '0:00') return sum;
                        const [h, m] = r.sked_hours.split(':').map(Number);
                        return sum + h + (m / 60);
                      }, 0);
                      const hours = Math.floor(total);
                      const minutes = Math.round((total - hours) * 60);
                      return `${hours}:${minutes.toString().padStart(2, '0')}`;
                    })()}
                  </span>
                </td>
                
                {/* PAY TIME Total */}
                <td className="px-3 py-3 text-right">
                  <span className="font-mono text-xs font-bold text-foreground">
                    {(() => {
                      const total = rotations.reduce((sum, r) => {
                        if (!r.pay_time || r.pay_time === '0:00') return sum;
                        const [h, m] = r.pay_time.split(':').map(Number);
                        return sum + h + (m / 60);
                      }, 0);
                      const hours = Math.floor(total);
                      const minutes = Math.round((total - hours) * 60);
                      return `${hours}:${minutes.toString().padStart(2, '0')}`;
                    })()}
                  </span>
                </td>
                
                {/* CREDIT Total */}
                <td className="px-3 py-3 text-right">
                  <span className="font-mono text-sm font-bold text-accent-cyan">
                    {(() => {
                      const total = rotations.reduce((sum, r) => {
                        if (!r.credit_hours || r.credit_hours === '0:00') return sum;
                        const [h, m] = r.credit_hours.split(':').map(Number);
                        return sum + h + (m / 60);
                      }, 0);
                      const hours = Math.floor(total);
                      const minutes = Math.round((total - hours) * 60);
                      return `${hours}:${minutes.toString().padStart(2, '0')}`;
                    })()}
                  </span>
                </td>
                
                {/* ADDTL PAY ONLY Total */}
                <td className="px-3 py-3 text-right">
                  <span className="font-mono text-xs font-bold text-foreground">
                    {(() => {
                      const total = rotations.reduce((sum, r) => {
                        if (!r.additional_pay_only || r.additional_pay_only === '0:00') return sum;
                        const [h, m] = r.additional_pay_only.split(':').map(Number);
                        return sum + h + (m / 60);
                      }, 0);
                      const hours = Math.floor(total);
                      const minutes = Math.round((total - hours) * 60);
                      return `${hours}:${minutes.toString().padStart(2, '0')}`;
                    })()}
                  </span>
                </td>
                
                {/* TAFB Total */}
                <td className="px-3 py-3 text-right">
                  <span className="font-mono text-xs font-bold text-foreground">
                    {(() => {
                      const total = rotations.reduce((sum, r) => {
                        if (!r.tafb_hours || r.tafb_hours === '0:00') return sum;
                        const [h, m] = r.tafb_hours.split(':').map(Number);
                        return sum + h + (m / 60);
                      }, 0);
                      const hours = Math.floor(total);
                      const minutes = Math.round((total - hours) * 60);
                      return `${hours}:${minutes.toString().padStart(2, '0')}`;
                    })()}
                  </span>
                </td>
                
                {/* Empty cells for Special Code and Tags */}
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="card p-3 bg-gradient-to-br from-accent-cyan/10 to-accent-cyan/5 border-accent-cyan/30">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Clock" className="w-4 h-4 text-accent-cyan" />
            <h3 className="text-xs font-medium text-muted-foreground uppercase">Avg Credit</h3>
          </div>
          <p className="text-xl font-bold text-accent-cyan">
            {(() => {
              const total = rotations.reduce((sum, r) => {
                if (!r.credit_hours || r.credit_hours === '0:00') return sum;
                const [h, m] = r.credit_hours.split(':').map(Number);
                return sum + h + (m / 60);
              }, 0);
              return (total / rotations.length).toFixed(1);
            })()}h
          </p>
          <p className="text-xs text-muted-foreground">per rotation</p>
        </div>

        <div className="card p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Plane" className="w-4 h-4 text-accent-purple" />
            <h3 className="text-xs font-medium text-muted-foreground uppercase">Flying Days</h3>
          </div>
          <p className="text-xl font-bold text-foreground">
            {rotations.filter(r => r.block_hours && r.block_hours !== '0:00').length}
          </p>
          <p className="text-xs text-muted-foreground">
            of {rotations.length} days
          </p>
        </div>

        <div className="card p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="AlertTriangle" className="w-4 h-4 text-warning" />
            <h3 className="text-xs font-medium text-muted-foreground uppercase">Special Codes</h3>
          </div>
          <p className="text-xl font-bold text-warning">
            {rotations.filter(r => r.is_special_code).length}
          </p>
          <p className="text-xs text-muted-foreground">entries</p>
        </div>

        <div className="card p-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Tag" className="w-4 h-4 text-accent-purple" />
            <h3 className="text-xs font-medium text-muted-foreground uppercase">Tagged Rots</h3>
          </div>
          <p className="text-xl font-bold text-accent-purple">
            {rotations.filter(r => r.flags && r.flags.length > 0).length}
          </p>
          <p className="text-xs text-muted-foreground">have flags</p>
        </div>
      </div>

      {/* Symbol Legend */}
      <div className="card p-4 bg-muted/30">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Info" className="w-4 h-4 text-accent-cyan" />
          Symbol Legend
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Icon name="ArrowRightLeft" className="w-4 h-4 text-warning flex-shrink-0" />
            <span className="text-muted-foreground">
              <span className="font-semibold text-foreground">Spillover</span> - Rotation split across months, partial credit shown
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Link" className="w-4 h-4 text-accent-purple flex-shrink-0" />
            <span className="text-muted-foreground">
              <span className="font-semibold text-foreground">Linked</span> - Connected to parsed rotation data
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RotationsList;
